// Inventory-Index fuer die TopBar-Suche. Stellt die Liste aller
// homelab-spezifischen Entitaeten bereit, die durchsucht werden
// koennen:
//   - Tasks         (Cron-Jobs, /tasks)
//   - Automations   (Regeln, /system/automatiken)
//   - Hosts         (Cluster-Mitglieder, /infra/cluster/<id>)
//   - Agents        (homelab-agent + Push-Agenten, /infra/agents)
//   - Container     (Docker-Container, /infra/docker)
//   - Zigbee/ESPHome/Z-Wave (vereinheitlichte Geraete-Seite /zuhause/geraete)
//
// ALLE Quellen sind die schon laufenden Boot-Stores (tasks-list,
// cluster-stats, automations-, agents-, docker-, zigbee-, esphome-,
// zwave-summary). NavSearchPanel zieht das gesammelte Ergebnis als
// reaktive Liste und reicht es ggf. an die semantische Suche weiter
// (addEntities) -- so werden auch neue Tasks im laufenden Betrieb
// embedded, ohne dass jemand reloaden muss.
//
// Eigene Abrufe hat dieses Modul nicht mehr: Tasks und Hosts holte es
// bis 23.08.2026 selbst, obwohl beide Listen bereits im Haus waren.
// Damit faellt auch der eigene 5-Minuten-Takt weg -- der Index sieht
// jetzt, was die Stores sehen, und zwar sobald sie es sehen.
//
// Zigbee, ESPHome und Automatiken liest sonst niemand: ihre Stores holen die
// Namen einmal beim Start (Suche und Embedding-Index) und danach nur im Takt,
// solange jemand sucht (beobachteInventar, lib/bei-bedarf.ts).

import { getAgents } from "./agents-summary.svelte";
import { aufgabenPfad, aufgabenSeite } from "./aufgaben-pfad";
import { beobachteAutomations, getAutomationsSummary } from "./automations-summary.svelte";
import { getClusterStats, startClusterStats } from "./cluster-stats.svelte";
import { getDockerHosts } from "./docker-summary.svelte";
import { beobachteEsphome, getEsphomeSummary } from "./esphome-summary.svelte";
import { getTasksList, startTasksList } from "./tasks-list.svelte";
import { getToolsEntries } from "./tools-summary.svelte";
import { beobachteZigbee, getZigbeeSummary } from "./zigbee-summary.svelte";
import { getZwaveSummary } from "./zwave-summary.svelte";
import type { TaskDescription, TaskItem } from "./types";

export interface InventoryEntry {
  kind:
    | "task"
    | "automation"
    | "host"
    | "agent"
    | "container"
    | "zigbee"
    | "esphome"
    | "zwave"
    | "oberflaeche";
  // searchKey ist eindeutig pro Entry (z.B. "task:wob-data-backup").
  // Wird als Lookup im Embedding-Cache verwendet, NICHT als
  // Navigations-Ziel -- dafuer ist href da.
  searchKey: string;
  // label = primaerer Treffer-Titel ("WOB Data Backup").
  label: string;
  // aliases = zusaetzliche Such-Stichworte (Typ, Labels, Server).
  aliases: string[];
  // detail = rechtsbuendige Badge ("zfs_backup_chain · forge").
  detail: string;
  // embedDetail = STABILE Variante des Details fuer den Embedding-Text.
  // detail darf Live-Werte tragen (ON/OFF, Batterie-%); im Embed-Text
  // erzeugt jeder Wertwechsel einen neuen Cache-Key -> Cache-Miss ->
  // bge-m3 embedded bei jedem Suchfeld-Fokus neu. Fehlt embedDetail,
  // gilt detail als stabil.
  embedDetail?: string;
  // embedAliases = STABILE Aliase fuer den Embedding-Text, aus demselben
  // Grund: aliases darf Zustaende tragen (running/exited, online/offline),
  // damit die Wortsuche sie findet. Fehlt embedAliases, gelten aliases
  // als stabil.
  embedAliases?: string[];
  // page = Quell-Page ("Tasks", "Docker", ...). Wird im
  // NavSearchPanel als subtiler Hint zwischen Label und Badge
  // gerendert, damit der User sieht wo das Element herkommt.
  page: string;
  // href = wo der Klick hinfuehrt (Page + Focus-Query-Param).
  href: string;
  // intent = natuerlichsprachige "was tut das"-Zeile (heute nur aus
  // der Task-description.what). Fliesst in den Embed-Text (Semantik)
  // UND als Fuzzy-Intent-Haystack, damit Absichts-Queries ("openwrt
  // image bauen") die Zielseite auch ohne exakten Namens-Match finden.
  // Unsichtbar in der UI.
  intent?: string;
}

// taskIntent baut die verb-reiche Suchzeile aus dem strukturierten
// TaskDescription-Block. `what` ist der Kern ("Baut custom OpenWrt-
// Images ..."), `when` gibt Ausloese-Vokabular ("naechtlich", "bei
// Boot") dazu. skips/on_failure lassen wir raus -- das ist Negativ-
// Text, der als Such-Anker mehr Rauschen als Signal bringt.
function taskIntent(d: TaskDescription | null | undefined): string {
  if (!d) return "";
  return [d.what, d.when].filter(Boolean).join(" ").trim();
}

interface ClusterStatsHostRaw {
  id: string;
  name: string;
  ip?: string;
  role?: string;
  os?: string;
}

interface ClusterStatsRaw {
  cluster?: string;
  cluster_name?: string;
  name?: string;
  hosts?: ClusterStatsHostRaw[];
}

// Tasks und Hosts kommen aus den Stores, die ohnehin laufen -- dieses
// Modul holt sie NICHT selbst.
//
// Vorher tat es das: /v2-api/tasks (204 KB) und /v2-api/cluster/stats
// wurden hier ein zweites Mal geladen, obwohl tasks-list und
// cluster-stats sie gerade erst geholt hatten. Im Boot-Wasserfall vom
// 23.08.2026 stand /v2-api/tasks dadurch dreimal. Der Index ist ein
// LESER der Domaenen-Stores, kein eigener Sammler -- und bekommt so
// deren WS-Aktualisierungen gratis mit, statt auf seinen 5-Minuten-Takt
// zu warten.
let _started = false;

function _tasks(): TaskItem[] {
  return getTasksList() ?? [];
}

function _hosts(): { host: ClusterStatsHostRaw; cluster: string }[] {
  const flat: { host: ClusterStatsHostRaw; cluster: string }[] = [];
  for (const c of (getClusterStats() ?? []) as ClusterStatsRaw[]) {
    const clusterName = c.cluster ?? c.cluster_name ?? c.name ?? "";
    for (const h of c.hosts ?? []) {
      flat.push({ host: h, cluster: clusterName });
    }
  }
  return flat;
}

export function startInventoryIndex(): void {
  if (_started) return;
  _started = true;
  // Die Quellen-Stores starten in startBootStreams(); defensiv hier
  // noch einmal, damit ein Direktaufruf nicht auf leere Listen sieht.
  startTasksList();
  startClusterStats();
}

export function stopInventoryIndex(): void {
  _started = false;
}

// Anmelden, solange die Suche offen ist (NavSearchPanel) oder die Seite
// Einstellungen › Suche zu sehen ist; der Rückgabewert meldet wieder ab.
// Hält Zigbee, ESPHome und Automatiken frisch. In Svelte:
// $effect(() => beobachteInventar()).
export function beobachteInventar(): () => void {
  const ab = [beobachteZigbee(), beobachteEsphome(), beobachteAutomations()];
  return () => {
    for (const f of ab) f();
  };
}

// inventoryEntries: reaktive Liste fuer NavSearchPanel. Bezieht die
// Daten aus den laufenden Domaenen-Stores (Tasks, Cluster, Automations,
// Agents) -- dieses Modul haelt keinen eigenen Datenbestand.
// Konsumenten erhalten bei jedem Refresh automatisch das neue Set.
export function inventoryEntries(): InventoryEntry[] {
  const out: InventoryEntry[] = [];

  for (const t of _tasks()) {
    const labels = t.labels ?? [];
    const detailParts = [t.type, t.server].filter(Boolean);
    // Der Suchtreffer fuehrt dorthin, wo die Aufgabe steht: Aufgaben-
    // Seite, Kette auf der Backup-Seite oder Werkzeug-Katalog
    // (lib/aufgaben-pfad.ts).
    out.push({
      kind: "task",
      searchKey: `task:${t.id}`,
      label: t.name,
      aliases: [t.type, ...labels, t.id].filter(Boolean),
      detail: detailParts.join(" · "),
      page: aufgabenSeite(t),
      href: aufgabenPfad(t),
      intent: taskIntent(t.description),
    });
  }

  const autos = getAutomationsSummary();
  if (autos) {
    for (const a of autos.rules) {
      out.push({
        kind: "automation",
        searchKey: `automation:${a.name}`,
        label: a.name,
        aliases: [a.mode, ...(a.targets ?? [])].filter(Boolean),
        detail: a.mode || "automation",
        page: "Automatisierungen",
        href: `/system/automatiken?focus=${encodeURIComponent(a.name)}`,
      });
    }
  }

  for (const entry of _hosts()) {
    const h = entry.host;
    const aliases = [h.id, h.role, h.os, h.ip, entry.cluster].filter(Boolean) as string[];
    out.push({
      kind: "host",
      searchKey: `host:${h.id}`,
      label: h.name,
      aliases,
      detail: [entry.cluster, h.role].filter(Boolean).join(" · "),
      page: "Cluster",
      href: `/infra/cluster/${encodeURIComponent(h.id)}`,
    });
  }

  const agents = getAgents();
  for (const a of agents) {
    out.push({
      kind: "agent",
      searchKey: `agent:${a.id}`,
      label: a.name,
      aliases: [a.kind, a.ssh_alias].filter(Boolean),
      detail: a.kind || "agent",
      page: "Agents",
      href: `/infra/agents?focus=${encodeURIComponent(a.id)}`,
    });
  }

  // Docker-Container: ein Eintrag pro (host, container). Mehrere
  // Hosts mit gleichnamigem Container (z.B. "watchtower" auf allen
  // Forge-Nodes) erzeugen mehrere Treffer -- absichtlich, damit der
  // User sehen kann, wo der Container laeuft. searchKey traegt den
  // Host mit, damit der Embedding-Cache sie trennen kann.
  for (const h of getDockerHosts()) {
    for (const c of h.containers) {
      const stack = c.stack ?? "";
      const stabil = [c.image, stack, h.host_id, h.host_name].filter(Boolean) as string[];
      out.push({
        kind: "container",
        searchKey: `container:${h.host_id}:${c.name}`,
        label: c.name,
        aliases: [...stabil, c.state].filter(Boolean),
        embedAliases: stabil,
        detail: [h.host_name || h.host_id, stack].filter(Boolean).join(" · "),
        page: "Docker",
        // href ohne ?q= -- NavSearchPanel.qForItem haengt den Filter
        // selbst an (Label -> Docker-Page filtert nach Name).
        href: `/infra/docker`,
      });
    }
  }

  // Zigbee-Bridge-Sicht: alle Geraete von z2m, inkl. Sensoren die
  // sonst nur in klima-summary auftauchen. Doppelter Treffer mit
  // aktoren/sensoren ist OK -- die Ziel-Page ist eine andere
  // (Bridge-Diagnostik vs. Aktor-Steuerung), und das page-Feld macht
  // den Unterschied sichtbar.
  const zb = getZigbeeSummary();
  if (zb) {
    for (const name of Object.keys(zb)) {
      const st = zb[name];
      // Aliases STABIL halten (kein st.state="ON"/"OFF" -- Live-Werte im
      // Embed-Text triggern bge-m3 bei jedem Wertwechsel neu, s.o.).
      const aliases = [
        st.state != null ? "schalter" : "",
        st.battery !== undefined ? "battery" : "",
        st.temperature !== undefined ? "sensor" : "",
      ].filter(Boolean) as string[];
      out.push({
        kind: "zigbee",
        searchKey: `zigbee:${name}`,
        label: name,
        aliases,
        detail: st.state || (st.battery !== undefined ? `${st.battery}%` : "zigbee"),
        embedDetail: "zigbee",
        page: "Zigbee",
        href: `/zuhause/geraete`,
      });
    }
  }

  const esp = getEsphomeSummary();
  if (esp) {
    for (const d of esp) {
      out.push({
        kind: "esphome",
        searchKey: `esphome:${d.id}`,
        label: d.id,
        aliases: [d.category, d.online ? "online" : "offline"].filter(Boolean),
        embedAliases: [d.category].filter(Boolean),
        detail: d.category || "esphome",
        page: "ESPHome",
        href: `/zuhause/geraete`,
      });
    }
  }

  const zw = getZwaveSummary();
  if (zw) {
    for (const d of zw) {
      const stabil = d.node_id !== undefined ? [String(d.node_id)] : [];
      out.push({
        kind: "zwave",
        searchKey: `zwave:${d.name}`,
        label: d.name,
        aliases: [...stabil, d.online ? "online" : "offline"],
        embedAliases: stabil,
        detail: d.node_id !== undefined ? `node ${d.node_id}` : "zwave",
        page: "Z-Wave",
        href: `/zuhause/geraete`,
      });
    }
  }

  // Weboberflächen (Werkzeuge › Oberflächen): wer "z2m", "AdGuard" oder
  // eine Adresse sucht, landet auf der Zeile mit Link und Anmeldung.
  for (const w of getToolsEntries() ?? []) {
    if (w.kind !== "oberflaeche") continue;
    const adresse = w.url ? w.url.replace(/^https?:\/\//, "") : "";
    out.push({
      kind: "oberflaeche",
      searchKey: `oberflaeche:${w.id}`,
      label: w.name,
      aliases: ["Weboberfläche", "Oberfläche", "Adresse", w.host ?? "", adresse].filter(Boolean),
      detail: w.host ?? "",
      page: "Werkzeuge",
      href: `/system/werkzeuge?focus=${encodeURIComponent(w.id)}`,
      intent: w.description,
    });
  }

  return out;
}

// inventoryEmbedEntries: gleicher Output wie inventoryEntries, aber
// im Format {key, text} fuer addEntities(). Text = "label aliases
// detail intent". Der intent-Teil (Task-description.what) traegt die
// Verben, an denen bge-m3 Absichts-Queries festmacht.
export function inventoryEmbedEntries(): Array<{ key: string; text: string }> {
  return inventoryEntries().map((e) => ({
    key: e.searchKey,
    // embedDetail/embedAliases statt detail/aliases, wo diese Live-Werte
    // tragen -- der Embed-Text MUSS stabil sein (Ablage-Key = der Text).
    text: [
      e.label,
      (e.embedAliases ?? e.aliases).join(" "),
      e.embedDetail ?? e.detail,
      e.intent ?? "",
    ]
      .filter(Boolean)
      .join(" "),
  }));
}
